import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getAuthHeader } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Camera, Save, ChevronDown, ChevronUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function StudentForm() {
  const { tourId, studentId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const isEdit = !!studentId && studentId !== "new";
  const [showMedical, setShowMedical] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name: "", dob: "", age: "", gender: "", student_id: "", class_name: "", section: "",
    blood_group: "", photo_url: "", emergency_contact_name: "", emergency_contact_phone: "",
    medical_conditions: "", allergies: "", medications: "", special_needs: "",
    consent_photo: false, consent_medical: false, declaration: false,
  });

  useEffect(() => {
    if (isEdit) loadStudent();
  }, [studentId]);

  const loadStudent = async () => {
    try {
      const { data } = await axios.get(`${API}/api/portal/tours/${tourId}/students`, { headers: getAuthHeader() });
      const s = data.find(st => st.id === studentId);
      if (s) {
        setForm(s);
        if (s.photo_url) setPhotoPreview(s.photo_url.startsWith('/') ? `${API}${s.photo_url}` : s.photo_url);
      }
    } catch { toast.error("Failed to load student"); }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file));
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/api/portal/upload`, fd, { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } });
      setForm(p => ({ ...p, photo_url: data.url }));
      toast.success("Photo uploaded");
    } catch { toast.error("Photo upload failed"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name) { toast.error("Full name is required"); return; }
    if (!form.consent_photo || !form.consent_medical || !form.declaration) { toast.error("All consent checkboxes are mandatory"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/api/portal/tours/${tourId}/students/${studentId}`, form, { headers: getAuthHeader() });
        toast.success("Student updated");
      } else {
        await axios.post(`${API}/api/portal/tours/${tourId}/students`, form, { headers: getAuthHeader() });
        toast.success("Student added");
      }
      navigate(`/portal/tours/${tourId}`);
    } catch { toast.error("Failed to save student"); }
    finally { setSaving(false); }
  };

  const u = (field, value) => setForm(p => ({ ...p, [field]: value }));

  return (
    <div className="min-h-screen bg-background" data-testid="student-form-page">
      <header className="bg-primary text-primary-foreground px-6 py-4">
        <div className="max-w-3xl mx-auto">
          <Link to={`/portal/tours/${tourId}`} className="inline-flex items-center gap-1.5 text-sm text-primary-foreground/70 hover:text-primary-foreground mb-2">
            <ArrowLeft className="w-4 h-4" /> Back to Tour
          </Link>
          <h1 className="font-heading text-xl font-medium">{isEdit ? "Edit Student" : "Add Student"}</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Photo Upload */}
          <div className="flex justify-center">
            <div onClick={() => fileRef.current?.click()} className="w-32 h-32 rounded-sm border-2 border-dashed border-border/60 bg-card flex items-center justify-center cursor-pointer hover:border-primary overflow-hidden">
              {photoPreview ? (
                <img src={photoPreview} alt="Student" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center">
                  <Camera className="w-8 h-8 text-muted-foreground/40 mx-auto mb-1" />
                  <span className="text-xs text-muted-foreground">Tap to upload</span>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} className="hidden" />
          </div>

          {/* Basic Info */}
          <div className="bg-card border border-border/60 p-6 rounded-sm space-y-4">
            <h3 className="font-heading text-lg font-medium">Basic Information</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-xs font-medium mb-1 block">Full Name *</label><Input value={form.full_name} onChange={e => u("full_name", e.target.value.replace(/[^a-zA-Z\s]/g, ''))} placeholder="Student's full name" /></div>
              <div><label className="text-xs font-medium mb-1 block">Student ID / Roll Number</label><Input value={form.student_id} onChange={e => u("student_id", e.target.value)} placeholder="Roll number" /></div>
              <div><label className="text-xs font-medium mb-1 block">Date of Birth</label><Input type="date" value={form.dob} onChange={e => u("dob", e.target.value)} /></div>
              <div><label className="text-xs font-medium mb-1 block">Age</label><Input value={form.age} onChange={e => u("age", e.target.value.replace(/[^0-9]/g, ''))} placeholder="Age" /></div>
              <div>
                <label className="text-xs font-medium mb-1 block">Gender</label>
                <select value={form.gender} onChange={e => u("gender", e.target.value)} className="w-full h-10 border border-border/60 rounded-sm px-3 text-sm bg-card">
                  <option value="">Select</option><option>Male</option><option>Female</option><option>Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">Class</label>
                <select value={form.class_name} onChange={e => u("class_name", e.target.value)} className="w-full h-10 border border-border/60 rounded-sm px-3 text-sm bg-card">
                  <option value="">Select</option>
                  {["Nursery","LKG","UKG","1","2","3","4","5","6","7","8","9","10","11","12"].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">Section</label>
                <select value={form.section} onChange={e => u("section", e.target.value)} className="w-full h-10 border border-border/60 rounded-sm px-3 text-sm bg-card">
                  <option value="">Select</option>
                  {["A","B","C","D","E","F"].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">Blood Group</label>
                <select value={form.blood_group} onChange={e => u("blood_group", e.target.value)} className="w-full h-10 border border-border/60 rounded-sm px-3 text-sm bg-card">
                  <option value="">Select</option>
                  {["A+","A-","B+","B-","AB+","AB-","O+","O-"].map(b => <option key={b}>{b}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="bg-card border border-border/60 p-6 rounded-sm space-y-4">
            <h3 className="font-heading text-lg font-medium">Emergency Contact</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-xs font-medium mb-1 block">Contact Name</label><Input value={form.emergency_contact_name} onChange={e => u("emergency_contact_name", e.target.value)} placeholder="Parent/Guardian name" /></div>
              <div><label className="text-xs font-medium mb-1 block">Contact Phone</label><Input type="tel" value={form.emergency_contact_phone} onChange={e => u("emergency_contact_phone", e.target.value.replace(/[^0-9+\s-]/g, ''))} placeholder="+91 XXXXX XXXXX" /></div>
            </div>
          </div>

          {/* Medical (Collapsible) */}
          <div className="bg-card border border-border/60 rounded-sm">
            <button type="button" onClick={() => setShowMedical(!showMedical)}
              className="w-full flex items-center justify-between p-6 text-left">
              <h3 className="font-heading text-lg font-medium">Medical & Health Information</h3>
              {showMedical ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {showMedical && (
              <div className="px-6 pb-6 space-y-4">
                <p className="text-xs text-muted-foreground italic">This information is stored securely and visible only to authorized staff.</p>
                <div><label className="text-xs font-medium mb-1 block">Medical Conditions</label><Textarea value={form.medical_conditions} onChange={e => u("medical_conditions", e.target.value)} placeholder="Any known medical conditions..." /></div>
                <div><label className="text-xs font-medium mb-1 block">Allergies</label><Textarea value={form.allergies} onChange={e => u("allergies", e.target.value)} placeholder="Food, medicine, or other allergies..." /></div>
                <div><label className="text-xs font-medium mb-1 block">Current Medications</label><Input value={form.medications} onChange={e => u("medications", e.target.value)} placeholder="Any ongoing medications" /></div>
                <div><label className="text-xs font-medium mb-1 block">Special Needs</label><Input value={form.special_needs} onChange={e => u("special_needs", e.target.value)} placeholder="Any special needs or accommodations" /></div>
              </div>
            )}
          </div>

          {/* Consent */}
          <div className="bg-card border border-border/60 p-6 rounded-sm space-y-4">
            <h3 className="font-heading text-lg font-medium">Consent & Declaration</h3>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.consent_photo} onChange={e => u("consent_photo", e.target.checked)} className="mt-1 rounded" />
              <span className="text-sm text-muted-foreground">I consent to the use of this student's photograph on the website, internal records, or social media.</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.consent_medical} onChange={e => u("consent_medical", e.target.checked)} className="mt-1 rounded" />
              <span className="text-sm text-muted-foreground">I consent to medical treatment in case of emergency during the tour.</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.declaration} onChange={e => u("declaration", e.target.checked)} className="mt-1 rounded" />
              <span className="text-sm text-muted-foreground font-medium">I confirm all information is correct and the student is fit to travel.</span>
            </label>
          </div>

          {/* Submit */}
          <div className="flex gap-3">
            <button type="submit" disabled={saving} data-testid="save-student-btn"
              className="inline-flex items-center gap-2 px-8 py-3 bg-accent text-accent-foreground font-medium text-sm rounded-sm hover:bg-accent/90 disabled:opacity-50">
              <Save className="w-4 h-4" /> {saving ? "Saving..." : isEdit ? "Update Student" : "Save Student"}
            </button>
            <Link to={`/portal/tours/${tourId}`} className="px-6 py-3 border border-border/60 text-sm rounded-sm hover:bg-muted inline-flex items-center">Cancel</Link>
          </div>
        </form>
      </main>
    </div>
  );
}
